/**
 * User Migration Delete Dialog Component
 *
 * Confirmation dialog for deleting migration users.
 */

import { Loader2, AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import type { MigrationUser } from '@/types/user-migration';

/**
 * User Migration Delete Dialog Props
 */
interface UserMigrationDeleteDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback to close the dialog */
  onClose: () => void;
  /** User to delete */
  user: MigrationUser | null;
  /** Callback to confirm deletion */
  onConfirm: () => Promise<void>;
  /** Whether the deletion is in progress */
  isDeleting: boolean;
}

/**
 * User Migration Delete Dialog Component
 */
export function UserMigrationDeleteDialog({
  open,
  onClose,
  user,
  onConfirm,
  isDeleting,
}: UserMigrationDeleteDialogProps) {
  if (!user) return null;

  return (
    <AlertDialog open={open} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <AlertDialogTitle>Delete Migration User</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="space-y-2">
            <p>Are you sure you want to delete this migration user?</p>
            <div className="mt-4 p-3 bg-muted rounded-md space-y-1">
              <p className="text-sm font-medium text-foreground">{user.email}</p>
              {user.firstName || user.lastName ? (
                <p className="text-sm text-muted-foreground">
                  {user.firstName} {user.lastName}
                </p>
              ) : null}
              <p className="text-xs text-muted-foreground">ID: {user.cognitoId}</p>
            </div>
            <p className="text-sm text-destructive font-medium mt-4">
              This action cannot be undone.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={isDeleting}>
            {isDeleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              'Delete User'
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
